import { FileUploaderDropContainer, FileUploaderItem } from "@carbon/react";

export interface BasicInformationFileUploadProps {
  /** Already-translated field label, rendered as a `cds--label` above the drop zone — same
   * convention `MaterialFileUpload.tsx` uses for its own "Specification" label. */
  label: string;
  /** Already-translated drop-zone prompt (`companyProfile.uploadProductsCatalog`/`uploadBrief`). */
  uploadLabel: string;
  /** Name of the file currently on record for this slot (either just picked, or already saved on
   * `profile.files`), or `null` when nothing is set yet. */
  fileName: string | null;
  isUploading: boolean;
  onFileSelected: (file: File) => void;
  onFileRemoved: () => void;
  accept?: string[];
  /** Offers a delete button on the file, which Carbon only shows in its "edit" status. */
  removeLabel?: string;
}

/**
 * One logo-less upload slot on the Basic Information sub-form (Task 8) — used twice by
 * `BasicInformationForm.tsx`, once for the products-catalog file and once for the company-brief
 * file. Built on the SAME `FileUploaderDropContainer`/`FileUploaderItem` pair `MaterialFileUpload`
 * introduced (Task 6) rather than a new upload primitive, per this migration's own convention
 * (Task 3's gap-check: "Covered, reuse verbatim").
 *
 * Unlike the pre-Carbon form — which hid the drop zone entirely once a file existed
 * (`!hasProductCatalogFile && (...)`) — the drop zone here stays visible even after a file is
 * picked, matching `MaterialFileUpload`'s own deliberate choice (see that component's docstring):
 * the user can pick a replacement at any time. Not a validation-parity concern (there was never a
 * rule requiring exactly one file, or blocking a replacement); a small, non-load-bearing UX
 * improvement consistent with the rest of this migration.
 */
const BasicInformationFileUpload = ({
  label,
  uploadLabel,
  fileName,
  isUploading,
  onFileSelected,
  onFileRemoved,
  accept,
  removeLabel,
}: BasicInformationFileUploadProps) => (
  <div className="basic-information-form__file-upload">
    <p className="basic-information-form__file-upload-label cds--label">
      {label}
    </p>
    <FileUploaderDropContainer
      labelText={uploadLabel}
      accept={accept}
      onAddFiles={(_event, { addedFiles }) => {
        const file = addedFiles[0];
        if (file) {
          onFileSelected(file);
        }
      }}
    />
    {fileName ? (
      <FileUploaderItem
        name={fileName}
        status={isUploading ? "uploading" : removeLabel ? "edit" : "complete"}
        iconDescription={removeLabel}
        onDelete={onFileRemoved}
      />
    ) : null}
  </div>
);

export default BasicInformationFileUpload;
